import { Box, Button, Grid, useTheme } from '@mui/material';
import {
  AutomaticCondition,
  AutomaticConditionAttributesTable,
  AutomaticConditionOverlap,
  CategoricalActions,
  ConditionOperator,
  ConditionType,
} from 'interfaces/automaticCondition';
import { OrchestrationDataType } from 'interfaces/calculateFeatures';
import { MappingFilterMetadata } from 'interfaces/postProcessingAction';
import { Study } from 'interfaces/study';
import { includes, isEmpty, isNumber, map, some } from 'es-toolkit/compat';
import React, { FunctionComponent } from 'react';
import AutomaticConditionBuilder from './AutomaticConditionBuilder';
import { isOverlap } from './utils';

interface AutomaticConditionGroupProps {
  operands: AutomaticCondition[];
  onOperandsChange: (operands: AutomaticCondition[]) => void;
  editable: boolean;
  study?: Study;
  orchestrationId?: string;
  shouldShowErrors?: boolean;
  featuresOnly?: boolean;
  singleOperand: boolean;
  conditionOptions: ConditionType[];
  mappingFiltersMetadata?: MappingFilterMetadata[];
  conditionOperators?: ConditionOperator[];
  logicalConditionRecursivelyNumLevels?: number;
  categoricalActions?: CategoricalActions[];
  orchestrationDataTypes?: OrchestrationDataType[];
}

export const defaultRangeCondition = { source: 'experimentResult' as const, field: '' };
export const defaultLogicCondition = {
  operator: ConditionOperator.AND,
  operands: [] as AutomaticCondition[],
};
export const defaultTagCondition = { tagId: '', isAssigned: true };
export const defaultAttributesTableCondition: AutomaticConditionAttributesTable = {
  conditionType: ConditionType.ATTRIBUTES_TABLE,
  mappingFilter: '',
  filterAction: '',
  toFilter: [],
  displayValue: '',
};

export const defaultOverlapCondition: AutomaticConditionOverlap = {
  conditionType: ConditionType.OVERLAP,
  filters: [] as AutomaticCondition[],
  minFraction: null,
  maxFraction: null,
};

const AutomaticConditionGroup: FunctionComponent<React.PropsWithChildren<AutomaticConditionGroupProps>> = ({
  operands,
  onOperandsChange,
  singleOperand,
  ...props
}) => {
  const theme = useTheme();
  const wrapWithLogicalCondition = () => {
    onOperandsChange([{ operator: ConditionOperator.AND, operands }]);
  };

  const addRangeCondition = () => {
    onOperandsChange([...operands, defaultRangeCondition]);
  };

  const addLogicalCondition = () => {
    onOperandsChange([...operands, defaultLogicCondition]);
  };

  const addTagCondition = () => {
    onOperandsChange([...operands, defaultTagCondition]);
  };

  const addAttributesTableCondition = () => {
    onOperandsChange([...operands, defaultAttributesTableCondition]);
  };

  const addOverlapCondition = () => {
    onOperandsChange([...operands, defaultOverlapCondition]);
  };

  const removeCondition = (index: number) => {
    const updatedConditions = [...operands];
    updatedConditions.splice(index, 1);
    onOperandsChange(updatedConditions);
  };

  const shouldDisplayAddButtons = !singleOperand || (singleOperand && isEmpty(operands));

  return (
    <Grid item>
      <Box
        width="100%"
        pl={1}
        my={1}
        p={1}
        bgcolor={theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800]}
        sx={
          isEmpty(operands)
            ? {
                border: 1,
                borderColor: 'error.main',
              }
            : undefined
        }
      >
        {!isEmpty(operands) &&
          includes(props.conditionOptions, ConditionType.LOGICAL) &&
          !isNumber(props.logicalConditionRecursivelyNumLevels) && (
            <Button
              disabled={!props.editable}
              variant="outlined"
              color="primary"
              size="small"
              onClick={wrapWithLogicalCondition}
              sx={{ mb: 1 }}
            >
              Wrap with Logical Condition
            </Button>
          )}
        {map(operands, (condition, index) => (
          <AutomaticConditionBuilder
            {...props}
            key={index}
            condition={condition}
            onConditionChange={(newCondition) => {
              const updatedConditions = [...operands];
              updatedConditions[index] = newCondition;
              onOperandsChange(updatedConditions);
            }}
            onRemove={() => {
              removeCondition(index);
            }}
          />
        ))}
        {isEmpty(operands) && <p>No conditions</p>}
      </Box>
      {shouldDisplayAddButtons && (
        <Grid item>
          {includes(props.conditionOptions, ConditionType.LOGICAL) && (
            <Button
              disabled={!props.editable}
              variant="outlined"
              color="primary"
              size="small"
              onClick={addLogicalCondition}
            >
              Add Logical Condition
            </Button>
          )}
          {includes(props.conditionOptions, ConditionType.RANGE) && (
            <Button
              disabled={!props.editable}
              variant="outlined"
              color="primary"
              size="small"
              onClick={addRangeCondition}
            >
              Add Range Condition
            </Button>
          )}
          {includes(props.conditionOptions, ConditionType.ATTRIBUTES_TABLE) && (
            <Button
              disabled={!props.editable}
              variant="outlined"
              color="primary"
              size="small"
              onClick={addAttributesTableCondition}
            >
              Add Attributes Table Condition
            </Button>
          )}
          {!props.featuresOnly && includes(props.conditionOptions, ConditionType.TAG) && (
            <Button
              disabled={!props.editable}
              variant="outlined"
              color="primary"
              size="small"
              onClick={addTagCondition}
            >
              Add Tag Condition
            </Button>
          )}
          {includes(props.conditionOptions, ConditionType.OVERLAP) &&
            !isEmpty(operands) &&
            !some(operands, (condition: AutomaticCondition) => isOverlap(condition)) && (
              <Button
                disabled={!props.editable}
                variant="outlined"
                color="primary"
                size="small"
                onClick={addOverlapCondition}
              >
                Add Overlap Condition
              </Button>
            )}
        </Grid>
      )}
    </Grid>
  );
};

export default AutomaticConditionGroup;
